import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import { API } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Vote, MessageSquare, Check, ChevronRight, Send } from 'lucide-react';

const COLORS = {
  primary: '#10B981',
  primaryDark: '#059669',
  primaryLight: '#D1FAE5',
  background: '#F9FAFB',
  white: '#FFFFFF',
  text: '#111827',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  blue: '#3B82F6',
  blueLight: '#DBEAFE'
};

export const AdminEventsWidget = ({ compact = false }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin-events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch admin events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (eventId, optionId) => {
    try {
      await axios.post(`${API}/admin-events/${eventId}/vote/${optionId}`);
      fetchEvents(); // Refresh to show updated votes
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to vote');
    }
  };

  const handleSubmitSuggestion = async (eventId, text) => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/admin-events/${eventId}/suggest`, { text });
      fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit suggestion');
    }
  };

  if (loading || events.length === 0) {
    return null;
  }

  // Compact view - just show count badge
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: COLORS.blueLight,
        borderRadius: 8,
        cursor: 'pointer'
      }} onClick={() => setSelectedEvent(events[0])}>
        <Vote size={16} color={COLORS.blue} />
        <span style={{ fontSize: 13, color: COLORS.blue, fontWeight: 500 }}>
          {events.length} Official Event{events.length > 1 ? 's' : ''} Active
        </span>
        <ChevronRight size={16} color={COLORS.blue} />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{
        fontSize: 14,
        fontWeight: 600,
        color: COLORS.text,
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <span style={{
          background: COLORS.blueLight,
          color: COLORS.blue,
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600
        }}>
          Official
        </span>
        Admin Events
      </h3>

      {events.map(event => (
        <AdminEventUserCard
          key={event.id}
          event={event}
          userId={user?.id}
          onVote={handleVote}
          onSubmitSuggestion={handleSubmitSuggestion}
        />
      ))}
    </div>
  );
};

const AdminEventUserCard = ({ event, userId, onVote, onSubmitSuggestion }) => {
  const [suggestionText, setSuggestionText] = useState('');
  const isVoting = event.event_type === 'voting';
  const totalVotes = isVoting ? event.options.reduce((sum, opt) => sum + opt.votes.length, 0) : 0;
  
  // Check if user has voted
  const userVote = isVoting ? event.options.find(opt => opt.votes.includes(userId)) : null;

  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      border: `1px solid ${COLORS.blue}`,
      borderLeftWidth: 4
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: COLORS.blueLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {isVoting ? <Vote size={20} color={COLORS.blue} /> : <MessageSquare size={20} color={COLORS.primary} />}
        </div>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: COLORS.text }}>
            {event.title}
          </h4>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.textSecondary }}>
            {event.description}
          </p>
        </div>
      </div>

      {/* Voting Options */}
      {isVoting && event.is_voting_open && (
        <div style={{ marginBottom: 8 }}>
          {event.options.map(opt => {
            const isSelected = opt.votes.includes(userId);
            const percentage = totalVotes > 0 ? Math.round((opt.votes.length / totalVotes) * 100) : 0;
            
            return (
              <button
                key={opt.id}
                onClick={() => onVote(event.id, opt.id)}
                data-testid={`vote-option-${opt.id}`}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: 8,
                  background: isSelected ? COLORS.primaryLight : COLORS.background,
                  border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
                  borderRadius: 8,
                  textAlign: 'left',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Progress bar background */}
                {userVote && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${percentage}%`,
                    background: isSelected ? COLORS.primaryLight : '#E5E7EB',
                    transition: 'width 0.3s'
                  }} />
                )}
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400, color: COLORS.text }}>
                    {isSelected && <Check size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                    {opt.text}
                  </span>
                  {userVote && (
                    <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                      {percentage}%
                    </span>
                  )}
                </div>
              </button>
            );
          })}
          <p style={{ fontSize: 11, color: COLORS.textMuted, textAlign: 'center' }}>
            {totalVotes} vote{totalVotes !== 1 ? 's' : ''} • {userVote ? 'You voted' : 'Click to vote'}
          </p>
        </div>
      )}

      {/* Suggestion Input */}
      {!isVoting && event.is_voting_open && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              value={suggestionText}
              onChange={(e) => setSuggestionText(e.target.value)}
              placeholder="Share your idea..."
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14
              }}
            />
            <button
              onClick={() => {
                onSubmitSuggestion(event.id, suggestionText);
                setSuggestionText('');
              }}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: 'none',
                background: COLORS.primary,
                color: COLORS.white,
                cursor: 'pointer'
              }}
            >
              <Send size={16} />
            </button>
          </div>
          <p style={{ fontSize: 11, color: COLORS.textMuted }}>
            {event.suggestions?.length || 0} suggestion{event.suggestions?.length !== 1 ? 's' : ''} submitted
          </p>
        </div>
      )}

      {/* Closed Status */}
      {!event.is_voting_open && (
        <div style={{
          padding: '8px 12px',
          background: COLORS.background,
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: 12, color: COLORS.textMuted }}>
            This event is closed
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminEventsWidget;
